import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select/Select';
import { Checkbox } from '@/components/ui/Checkbox/Checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { PageContainer } from '@/components/ui/PageContainer';
import { EventsService, type EventDTO } from '@/services/EventsService';
import { AttendeesService } from '@/services/AttendeesService';
import { ResearchService, type DestinationWithCount } from '@/services/ResearchService';
import { ClientsService, type ClientDTO } from '@/services/ClientsService';
import { useNotificationStore } from '@/stores/notificationStore';
import { apiFetch } from '@/lib/api';
import { flightCostService } from '@/services/FlightCostService';
import { 
  Users, 
  ArrowLeft, 
  Settings, 
  FileText, 
  Pencil,
  Sparkles,
  ChevronRight,
  Building2,
  Star,
  MapPin,
  Waves,
  UtensilsCrossed,
  Wine,
  Palmtree,
  Dumbbell,
  Flag,
  Calendar,
  Filter,
  DollarSign,
  User,
  Plane,
  StickyNote,
  Thermometer,
  Ruler,
  MessageSquare,
  X
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatEventDates, formatDateForInput } from '@/lib/dateFormatters';

// Helper to format filter values for display
function formatFilterValue(key: string, value: any): string | null {
  if (value === null || value === undefined) return null;
  
  switch (key) {
    case 'min_rooms':
      return `${value}+ rooms`;
    case 'min_conference_capacity':
      return `${value}+ conference capacity`;
    case 'star_rating':
      return `${value}+ stars`;
    case 'max_airport_distance_km':
      return `≤${value}km from airport`;
    case 'has_spa':
      return value ? 'Spa required' : null;
    case 'min_restaurants':
      return `${value}+ restaurants`;
    case 'min_bars':
      return `${value}+ bars`;
    case 'min_pools':
      return `${value}+ pools`;
    case 'beach_access':
      if (value === 'beachfront') return 'Beachfront';
      if (value === 'beach_access') return 'Beach access';
      return null;
    case 'all_inclusive':
      return value ? 'All-inclusive' : null;
    case 'property_type':
      if (value === 'adults_only') return 'Adults only';
      if (value === 'family_friendly') return 'Family friendly';
      return null;
    case 'has_golf':
      return value ? 'Golf course' : null;
    case 'min_tripadvisor_rating':
      return `${value}+ TripAdvisor`;
    default:
      return null;
  }
}

// Helper to get icon for filter
function getFilterIcon(key: string): React.ReactNode {
  switch (key) {
    case 'min_rooms':
      return <Building2 className="w-3.5 h-3.5" />;
    case 'star_rating':
      return <Star className="w-3.5 h-3.5" />;
    case 'max_airport_distance_km':
      return <MapPin className="w-3.5 h-3.5" />;
    case 'has_spa':
      return <Dumbbell className="w-3.5 h-3.5" />;
    case 'beach_access':
      return <Waves className="w-3.5 h-3.5" />;
    case 'min_restaurants':
      return <UtensilsCrossed className="w-3.5 h-3.5" />;
    case 'min_bars':
      return <Wine className="w-3.5 h-3.5" />;
    case 'min_pools':
      return <Palmtree className="w-3.5 h-3.5" />;
    case 'has_golf':
      return <Flag className="w-3.5 h-3.5" />;
    default:
      return null;
  }
}

interface ActionItem {
  title: string;
  description: string;
  icon: React.ReactNode;
  href?: string;
  onClick?: () => void;
  count?: number;
  comingSoon?: boolean;
}

export function EventDashboard() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const eventsSvc = useMemo(() => new EventsService(), []);
  const attendeesSvc = useMemo(() => new AttendeesService(), []);
  const researchSvc = useMemo(() => new ResearchService(), []);
  const clientsSvc = useMemo(() => new ClientsService(), []);
  const notifyError = useNotificationStore(s => s.error);
  const notifySuccess = useNotificationStore(s => s.success);
  
  const [loading, setLoading] = useState(false);
  const [event, setEvent] = useState<EventDTO | null>(null);
  const [client, setClient] = useState<ClientDTO | null>(null);
  const [attendeeCount, setAttendeeCount] = useState(0);
  const [flightCostCount, setFlightCostCount] = useState(0);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [destinations, setDestinations] = useState<DestinationWithCount[]>([]);
  const [loadingDestinations, setLoadingDestinations] = useState(false);
  
  // Filter values matching FilterValues interface from ClientEvents
  type FilterValues = {
    min_rooms?: number | null;
    min_conference_capacity?: number | null;
    star_rating?: number | null;
    max_airport_distance_km?: number | null;
    has_spa?: boolean | null;
    min_restaurants?: number | null;
    min_bars?: number | null;
    min_pools?: number | null;
    beach_access?: 'beachfront' | 'beach_access' | 'none' | null;
    all_inclusive?: boolean | null;
    property_type?: 'adults_only' | 'family_friendly' | 'mixed' | null;
    has_golf?: boolean | null;
    min_tripadvisor_rating?: number | null;
  };
  
  const [eventForm, setEventForm] = useState<{ 
    name: string; 
    description?: string;
    event_notes?: string;
    start_date?: string;
    end_date?: string;
    expected_attendees?: number;
    budget_min?: number;
    budget_max?: number;
    objective?: string;
    base_currency?: string;
    temperature_unit?: 'celsius' | 'fahrenheit';
    distance_unit?: 'kilometers' | 'miles';
    date_type?: 'date_range' | 'month';
    event_month?: string;
    date_display_override?: string;
    days_of_stay?: number;
  }>({ name: '', date_type: 'date_range' });
  
  // Objective options
  const objectiveOptions = [
    { value: '', label: 'None' },
    { value: 'strategic_alignment', label: 'Strategic Alignment' },
    { value: 'cross_department_bonding', label: 'Cross-department Bonding' },
    { value: 'reward_recognition', label: 'Reward & Recognition' },
    { value: 'team_building', label: 'Team Building' },
    { value: 'training_development', label: 'Training & Development' },
    { value: 'networking', label: 'Networking' },
    { value: 'celebration', label: 'Celebration' },
  ];

  // Currency options (top 10 most used currencies)
  const currencyOptions = [
    { value: 'USD', label: 'USD - US Dollar' },
    { value: 'EUR', label: 'EUR - Euro' },
    { value: 'GBP', label: 'GBP - British Pound' },
    { value: 'JPY', label: 'JPY - Japanese Yen' },
    { value: 'CAD', label: 'CAD - Canadian Dollar' },
    { value: 'AUD', label: 'AUD - Australian Dollar' },
    { value: 'CHF', label: 'CHF - Swiss Franc' },
    { value: 'CNY', label: 'CNY - Chinese Yuan' },
    { value: 'INR', label: 'INR - Indian Rupee' },
    { value: 'MXN', label: 'MXN - Mexican Peso' },
  ];

  // Temperature unit options
  const temperatureUnitOptions = [
    { value: 'celsius', label: 'Celsius (°C)' },
    { value: 'fahrenheit', label: 'Fahrenheit (°F)' },
  ];

  // Distance unit options
  const distanceUnitOptions = [
    { value: 'kilometers', label: 'Kilometers (km)' },
    { value: 'miles', label: 'Miles (mi)' },
  ];
  const [filters, setFilters] = useState<FilterValues>({});
  const [eventSaving, setEventSaving] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedback, setFeedback] = useState<Array<{
    id: string;
    name: string;
    comments: string;
    created_at: string;
  }>>([]);
  const [loadingFeedback, setLoadingFeedback] = useState(false);

  async function loadFeedback() {
    if (!eventId) return;
    setLoadingFeedback(true);
    try {
      const response = await apiFetch(`/api/events/${eventId}/proposal-feedback`);
      if (response.ok) {
        const result = await response.json();
        setFeedback(result.data || []);
      } else {
        const error = await response.json().catch(() => ({ message: 'Failed to load feedback' }));
        console.error('Failed to load feedback:', error);
      }
    } catch (err) {
      console.error('Failed to load feedback:', err);
    } finally {
      setLoadingFeedback(false);
    }
  }

  async function loadDestinations() {
    if (!eventId) return;
    setLoadingDestinations(true);
    try {
      const data = await researchSvc.listDestinations(eventId);
      setDestinations(data);
    } catch (e: any) {
      // Silently fail - destinations might not exist yet
      setDestinations([]);
    } finally {
      setLoadingDestinations(false);
    }
  }

  async function load() {
    if (!eventId) return;
    
    setLoading(true);
    try {
      const [eventData, attendeesData] = await Promise.all([
        eventsSvc.get(eventId),
        attendeesSvc.listByEvent(eventId, 1, 1) // Just need count
      ]);
      setEvent(eventData);
      setAttendeeCount(attendeesData.pagination?.totalItems || 0);
      // Flight cost estimates for this event (count only; failure just hides the badge).
      flightCostService.list(eventId).then((r) => setFlightCostCount(r.total)).catch(() => setFlightCostCount(0));
      
      // Load client data
      if (eventData.client_id) {
        try {
          const clientData = await clientsSvc.get(eventData.client_id);
          setClient(clientData);
        } catch (e: any) {
          // Silently fail - client might not exist
          console.error('Failed to load client:', e);
        }
      }
    } catch (e: any) {
      notifyError(e?.message || 'Failed to load event data');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [eventId]);
  
  // Load feedback count on mount
  useEffect(() => {
    if (eventId) {
      loadFeedback();
    }
  }, [eventId]);

  function updateFilter<K extends keyof FilterValues>(key: K, value: FilterValues[K]) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  // Format number with thousand separators
  function formatNumberWithCommas(value: number | undefined | null): string {
    if (value === null || value === undefined) return '';
    return value.toLocaleString('en-US');
  }

  // Parse number from formatted string (remove commas)
  function parseFormattedNumber(value: string): number | undefined {
    const cleaned = value.replace(/,/g, '');
    if (cleaned === '') return undefined;
    const num = parseFloat(cleaned);
    return isNaN(num) ? undefined : Math.round(num);
  }

  function openEdit() {
    if (!event) return;
    setEventForm({ 
      name: event.name || '', 
      description: event.description || undefined,
      event_notes: event.event_notes || undefined,
      start_date: formatDateForInput(event.start_date) || undefined,
      end_date: formatDateForInput(event.end_date) || undefined,
      expected_attendees: event.expected_attendees || undefined,
      budget_min: event.budget_min ? Math.round(event.budget_min) : undefined,
      budget_max: event.budget_max ? Math.round(event.budget_max) : undefined,
      objective: event.objective || undefined,
      base_currency: event.base_currency || 'USD',
      temperature_unit: event.temperature_unit || 'celsius',
      distance_unit: event.distance_unit || 'kilometers',
      date_type: (event.date_type as 'date_range' | 'month') || 'date_range',
      event_month: event.event_month || undefined,
      date_display_override: event.date_display_override || undefined,
      days_of_stay: event.days_of_stay || undefined,
    });
    
    // Load filters from event
    const eventFilters = event.venue_filters || {};
    setFilters({
      min_rooms: eventFilters.min_rooms ?? null,
      min_conference_capacity: eventFilters.min_conference_capacity ?? null,
      star_rating: eventFilters.star_rating ?? null,
      max_airport_distance_km: eventFilters.max_airport_distance_km ?? null,
      has_spa: eventFilters.has_spa ?? null,
      min_restaurants: eventFilters.min_restaurants ?? null,
      min_bars: eventFilters.min_bars ?? null,
      min_pools: eventFilters.min_pools ?? null,
      beach_access: eventFilters.beach_access ?? null,
      all_inclusive: eventFilters.all_inclusive ?? null,
      property_type: eventFilters.property_type ?? null,
      has_golf: eventFilters.has_golf ?? null,
      min_tripadvisor_rating: eventFilters.min_tripadvisor_rating ?? null,
    });
    
    setActiveTab('general');
    setEditModalOpen(true);
    loadDestinations();
  }

  function closeEditModal() {
    setEditModalOpen(false);
    setEventForm({ name: '', description: undefined, event_notes: undefined, objective: undefined, base_currency: 'USD', temperature_unit: 'celsius', distance_unit: 'kilometers', date_type: 'date_range' });
    setFilters({});
    setActiveTab('general');
    setDestinations([]);
  }

  async function submitEdit() {
    if (!event || !eventForm.name.trim()) return;
    
    setEventSaving(true);
    try {
      // Prepare venue_filters - only include non-null values
      const venueFilters: Record<string, any> = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          venueFilters[key] = value;
        }
      });
      const venueFiltersToSave = Object.keys(venueFilters).length > 0 ? venueFilters : null;
      
      await eventsSvc.update(event.id, {
        name: eventForm.name,
        description: eventForm.description || undefined,
        event_notes: eventForm.event_notes || undefined,
        start_date: eventForm.date_type === 'date_range' ? (eventForm.start_date || null) : null,
        end_date: eventForm.date_type === 'date_range' ? (eventForm.end_date || null) : null,
        expected_attendees: eventForm.expected_attendees != null ? eventForm.expected_attendees : null,
        budget_min: eventForm.budget_min !== undefined && eventForm.budget_min !== null ? Math.round(eventForm.budget_min) : undefined,
        budget_max: eventForm.budget_max !== undefined && eventForm.budget_max !== null ? Math.round(eventForm.budget_max) : undefined,
        objective: eventForm.objective || undefined,
        base_currency: eventForm.base_currency || null,
        temperature_unit: eventForm.temperature_unit || 'celsius',
        distance_unit: eventForm.distance_unit || 'kilometers',
        venue_filters: venueFiltersToSave,
        date_type: eventForm.date_type || 'date_range',
        event_month: eventForm.date_type === 'month' ? (eventForm.event_month || null) : null,
        date_display_override: eventForm.date_display_override || null,
        days_of_stay: eventForm.days_of_stay ?? null,
      });
      notifySuccess('Event updated successfully');
      closeEditModal();
      await load();
    } catch (e: any) {
      notifyError(e?.message || 'Failed to update event');
    } finally {
      setEventSaving(false);
    }
  }

  if (loading && !event) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      </PageContainer>
    );
  }

  if (!event) return null;

  // Parse venue filters for display
  const venueFilters = event.venue_filters || {};
  const filterEntries = Object.entries(venueFilters)
    .map(([key, value]) => ({
      key,
      value,
      label: formatFilterValue(key, value),
      icon: getFilterIcon(key),
    }))
    .filter(f => f.label !== null);
  
  const hasFilters = filterEntries.length > 0;
  const hasDescription = !!event.description;
  const hasEventNotes = !!event.event_notes;
  const hasEventDetails = !!(event.start_date || event.end_date || event.event_month || event.date_display_override || event.expected_attendees || event.budget_min || event.budget_max || event.base_currency || event.temperature_unit || event.distance_unit);
  const hasSettings = hasFilters || hasDescription || hasEventNotes || hasEventDetails;
  
  // Format date range for display (from-to format with short month)
  function formatDateRange(startDate: string | null | undefined, endDate: string | null | undefined): string {
    if (!startDate && !endDate) return '';
    
    const formatDate = (dateString: string): { month: string; day: string; year: string } => {
      try {
        const date = new Date(dateString);
        const month = date.toLocaleDateString('en-US', { month: 'short' });
        const day = date.toLocaleDateString('en-US', { day: 'numeric' });
        const year = date.toLocaleDateString('en-US', { year: 'numeric' });
        return { month, day, year };
      } catch {
        return { month: '', day: '', year: '' };
      }
    };
    
    if (startDate && endDate) {
      const start = formatDate(startDate);
      const end = formatDate(endDate);
      if (start.year === end.year) {
        return `${start.month} ${start.day} - ${end.month} ${end.day}, ${start.year}`;
      } else {
        return `${start.month} ${start.day}, ${start.year} - ${end.month} ${end.day}, ${end.year}`;
      }
    } else if (startDate) {
      const start = formatDate(startDate);
      return `From ${start.month} ${start.day}, ${start.year}`;
    } else if (endDate) {
      const end = formatDate(endDate);
      return `Until ${end.month} ${end.day}, ${end.year}`;
    }
    return '';
  }
  
  // Format budget range (no decimals, thousand separators)
  function formatBudgetRange(min: number | null | undefined, max: number | null | undefined): string {
    if (!min && !max) return '';
    const formatBudget = (value: number): string => {
      return Math.round(value).toLocaleString('en-US');
    };
    const formattedMin = min ? formatBudget(min) : '';
    const formattedMax = max ? formatBudget(max) : '';
    if (formattedMin && formattedMax) {
      return `$${formattedMin} - $${formattedMax}`;
    } else if (formattedMin) {
      return `$${formattedMin}+`;
    } else if (formattedMax) {
      return `Up to $${formattedMax}`;
    }
    return '';
  }

  const actionItems: ActionItem[] = [
    {
      title: 'Attendees',
      description: 'Manage event attendees and registrations',
      icon: <Users className="w-5 h-5" />,
      href: `/organization/events/${eventId}/attendees`,
      count: attendeeCount,
      onClick: () => navigate(`/organization/events/${eventId}/attendees`),
    },
    {
      title: 'Proposal',
      description: 'AI-powered venue research and proposal generation',
      icon: <Sparkles className="w-5 h-5" />,
      href: `/tools/research-proposal/${eventId}`,
      onClick: () => navigate(`/tools/research-proposal/${eventId}`),
    },
    {
      title: 'Proposal Feedback',
      description: 'View feedback submitted by clients',
      icon: <MessageSquare className="w-5 h-5" />,
      href: '#',
      count: feedback.length,
      onClick: () => {
        loadFeedback();
        setFeedbackModalOpen(true);
      },
    },
    {
      title: 'Flight Manifests',
      description: 'Upload and review passenger flight data',
      icon: <FileText className="w-5 h-5" />,
      href: `/tools/flight-manifests/${eventId}`,
      onClick: () => navigate(`/tools/flight-manifests/${eventId}`),
    },
    {
      title: 'Flight Cost Estimates',
      description: 'Compare destinations by estimated airfare per traveller',
      icon: <Plane className="w-5 h-5" />,
      href: `/tools/flight-cost?event_id=${eventId}`,
      count: flightCostCount,
      onClick: () => navigate(`/tools/flight-cost?event_id=${encodeURIComponent(eventId!)}`),
    },
    {
      title: 'Event Management',
      description: 'Scheduling, logistics, and coordination',
      icon: <Settings className="w-5 h-5" />,
      href: '#',
      comingSoon: true,
    },
  ];

  return (
    <PageContainer>
      <PageHeader 
        title={client && event ? `${client.name} - ${event.name}` : (event?.name || 'Event')}
        description="Event overview and management"
      >
        <div className="flex gap-2">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate(`/organization/clients/${event.client_id}`)}
            leftIcon={<ArrowLeft className="w-4 h-4" />}
          >
            Back to Client
          </Button>
          <Button 
            size="sm"
            onClick={openEdit}
            leftIcon={<Pencil className="w-4 h-4" />}
          >
            Edit Event
          </Button>
        </div>
      </PageHeader>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column: Event Settings */}
        <div className="space-y-6">
          <Card>
            <CardContent className="p-6">
              {hasSettings ? (
                <div className="space-y-6">
                  {/* Event Details */}
                  {hasEventDetails && (
                    <div className="space-y-4">
                      <h3 className="text-sm font-medium text-gray-500">Event Details</h3>
                      <div className="space-y-3">
                        {(event.start_date || event.end_date || event.event_month || event.date_display_override) && (
                          <div className="flex items-start gap-3">
                            <Calendar className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                            <div>
                              <div className="text-xs text-gray-500">Date</div>
                              <div className="text-sm font-medium text-gray-900">{formatEventDates(event, false)}</div>
                            </div>
                          </div>
                        )}
                        {event.expected_attendees && (
                          <div className="flex items-start gap-3">
                            <User className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                            <div>
                              <div className="text-xs text-gray-500">Expected Attendees</div>
                              <div className="text-sm font-medium text-gray-900">{event.expected_attendees.toLocaleString()}</div>
                            </div>
                          </div>
                        )}
                        {(event.budget_min || event.budget_max) && (
                          <div className="flex items-start gap-3">
                            <DollarSign className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                            <div>
                              <div className="text-xs text-gray-500">Budget</div>
                              <div className="text-sm font-medium text-gray-900">{formatBudgetRange(event.budget_min, event.budget_max)}</div>
                            </div>
                          </div>
                        )}
                        <div className="flex items-start gap-3">
                          <DollarSign className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <div className="text-xs text-gray-500">Currency</div>
                            <div className="text-sm font-medium text-gray-900">{event.base_currency || 'USD'}</div>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <Thermometer className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <div className="text-xs text-gray-500">Temperature Unit</div>
                            <div className="text-sm font-medium text-gray-900">
                              {event.temperature_unit === 'fahrenheit' ? 'Fahrenheit (°F)' : 'Celsius (°C)'}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <Ruler className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <div className="text-xs text-gray-500">Distance Unit</div>
                            <div className="text-sm font-medium text-gray-900">
                              {event.distance_unit === 'miles' ? 'Miles (mi)' : 'Kilometers (km)'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Description */}
                  {hasDescription && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-2">Description</h3>
                      <p className="text-gray-900">{event.description}</p>
                    </div>
                  )}
                  
                  {/* Requirements & Notes */}
                  {hasEventNotes && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-2">Requirements & Notes</h3>
                      <div className="flex items-start gap-3">
                        <StickyNote className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm text-gray-900 whitespace-pre-wrap">{event.event_notes}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Venue Filters */}
                  {hasFilters && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 mb-3">Venue Requirements</h3>
                      <div className="flex flex-wrap gap-2">
                        {filterEntries.map(({ key, label, icon }) => (
                          <Badge 
                            key={key} 
                            variant="secondary"
                            className="flex items-center gap-1.5 px-3 py-1.5"
                          >
                            {icon}
                            <span>{label}</span>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-gray-500 mb-3">No event settings configured yet.</p>
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={openEdit}
                    leftIcon={<Pencil className="w-4 h-4" />}
                  >
                    Add Settings
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column: Action Items */}
        <div className="space-y-4">
          {actionItems.map((item) => (
            <button
              key={item.title}
              onClick={item.comingSoon ? undefined : item.onClick}
              disabled={item.comingSoon}
              className={cn(
                "relative flex items-center gap-4 p-5 rounded-xl border text-left transition-all w-full",
                item.comingSoon
                  ? "bg-gray-50 border-gray-200 cursor-not-allowed opacity-60"
                  : "bg-white border-gray-200 hover:border-primary-300 hover:shadow-md cursor-pointer"
              )}
            >
              <div className={cn(
                "w-12 h-12 rounded-lg flex items-center justify-center shrink-0",
                item.comingSoon ? "bg-gray-100 text-gray-400" : "bg-primary-100 text-primary-600"
              )}>
                {item.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-900">{item.title}</h3>
                  {item.count !== undefined && (
                    <Badge variant="secondary" className="text-xs">
                      {item.count}
                    </Badge>
                  )}
                  {item.comingSoon && (
                    <Badge variant="outline" className="text-xs">
                      Coming Soon
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-0.5">{item.description}</p>
              </div>
              {!item.comingSoon && (
                <ChevronRight className="w-5 h-5 text-gray-400" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Edit Event Modal */}
      <Modal 
        isOpen={editModalOpen} 
        onClose={closeEditModal} 
        title="Edit Event"
        description="Update event information"
        size="xl"
      >
        <form onSubmit={(e) => { e.preventDefault(); submitEdit(); }}>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full grid grid-cols-3 mb-6">
              <TabsTrigger value="general" className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>General</span>
              </TabsTrigger>
              <TabsTrigger value="venue-requirements" className="flex items-center gap-2">
                <Filter className="w-4 h-4" />
                <span>Venue Requirements</span>
              </TabsTrigger>
              <TabsTrigger value="destinations" className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span>Destinations</span>
              </TabsTrigger>
            </TabsList>

            {/* General Tab */}
            <TabsContent value="general" className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Event Name *</label>
                <Input 
                  value={eventForm.name} 
                  onChange={(e) => setEventForm(f => ({ ...f, name: e.target.value }))} 
                  placeholder="e.g., 2024 Annual Company Retreat"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <Textarea 
                  value={eventForm.description || ''} 
                  onChange={(e) => setEventForm(f => ({ ...f, description: e.target.value }))} 
                  placeholder="Brief description of the event"
                  rows={3}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Requirements & Notes</label>
                <Textarea 
                  value={eventForm.event_notes || ''} 
                  onChange={(e) => setEventForm(f => ({ ...f, event_notes: e.target.value }))} 
                  placeholder="Describe event requirements, preferences, and constraints..."
                  rows={4}
                />
              </div>
              
              {/* Event Dates */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Event Dates</label>
                  <div className="flex gap-4 mb-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="date_type"
                        value="date_range"
                        checked={eventForm.date_type === 'date_range'}
                        onChange={() => setEventForm(f => ({ ...f, date_type: 'date_range', event_month: undefined }))}
                        className="w-4 h-4 text-primary-600"
                      />
                      <span className="text-sm">By Date Range</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="date_type"
                        value="month"
                        checked={eventForm.date_type === 'month'}
                        onChange={() => setEventForm(f => ({ ...f, date_type: 'month', start_date: undefined, end_date: undefined }))}
                        className="w-4 h-4 text-primary-600"
                      />
                      <span className="text-sm">By Month</span>
                    </label>
                  </div>
                  
                  {eventForm.date_type === 'date_range' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-muted-foreground mb-1">Start Date</label>
                        <Input 
                          type="date"
                          value={eventForm.start_date || ''} 
                          onChange={(e) => setEventForm(f => ({ ...f, start_date: e.target.value }))} 
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-muted-foreground mb-1">End Date</label>
                        <Input 
                          type="date"
                          value={eventForm.end_date || ''} 
                          onChange={(e) => setEventForm(f => ({ ...f, end_date: e.target.value }))} 
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-muted-foreground mb-1">Month</label>
                          <Select
                            value={eventForm.event_month ? eventForm.event_month.split('-')[1] : ''}
                            onChange={(e) => {
                              const year = eventForm.event_month?.split('-')[0] || new Date().getFullYear().toString();
                              setEventForm(f => ({ ...f, event_month: e.target.value ? `${year}-${e.target.value}` : undefined }));
                            }}
                          >
                            <option value="">Select month</option>
                            <option value="01">January</option>
                            <option value="02">February</option>
                            <option value="03">March</option>
                            <option value="04">April</option>
                            <option value="05">May</option>
                            <option value="06">June</option>
                            <option value="07">July</option>
                            <option value="08">August</option>
                            <option value="09">September</option>
                            <option value="10">October</option>
                            <option value="11">November</option>
                            <option value="12">December</option>
                          </Select>
                        </div>
                        <div>
                          <label className="block text-sm text-muted-foreground mb-1">Year</label>
                          <Select
                            value={eventForm.event_month ? eventForm.event_month.split('-')[0] : ''}
                            onChange={(e) => {
                              const month = eventForm.event_month?.split('-')[1] || '01';
                              setEventForm(f => ({ ...f, event_month: e.target.value ? `${e.target.value}-${month}` : undefined }));
                            }}
                          >
                            <option value="">Select year</option>
                            {Array.from({ length: 7 }, (_, i) => new Date().getFullYear() + i - 1).map(year => (
                              <option key={year} value={year}>{year}</option>
                            ))}
                          </Select>
                        </div>
                      </div>
                      <div className="mt-3">
                        <label className="block text-sm text-muted-foreground mb-1">Days of Stay</label>
                        <Input
                          type="number"
                          min={1}
                          max={365}
                          value={eventForm.days_of_stay || ''}
                          onChange={(e) => setEventForm(f => ({ ...f, days_of_stay: e.target.value ? Number(e.target.value) : undefined }))}
                          placeholder="Number of nights"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Used for budget calculations (room nights).
                        </p>
                      </div>
                    </>
                  )}
                </div>
                
                {/* Date Display Override */}
                <div>
                  <label className="block text-sm font-medium mb-1">Custom Date Display (optional)</label>
                  <Input 
                    type="text"
                    value={eventForm.date_display_override || ''} 
                    onChange={(e) => setEventForm(f => ({ ...f, date_display_override: e.target.value || undefined }))} 
                    placeholder="e.g., Spring 2026, Q2 2026, TBD"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Override how dates appear in proposals. Leave empty to use actual dates.
                  </p>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Expected Number of Attendees</label>
                <Input 
                  type="number"
                  min={0}
                  value={eventForm.expected_attendees || ''} 
                  onChange={(e) => setEventForm(f => ({ ...f, expected_attendees: e.target.value ? Number(e.target.value) : undefined }))} 
                  placeholder="e.g., 150"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Objective</label>
                <Select
                  value={eventForm.objective || ''}
                  onChange={(e) => setEventForm(f => ({ ...f, objective: e.target.value || undefined }))}
                >
                  {objectiveOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Budget Min</label>
                  <Input 
                    type="text"
                    value={formatNumberWithCommas(eventForm.budget_min)} 
                    onChange={(e) => {
                      const parsed = parseFormattedNumber(e.target.value);
                      setEventForm(f => ({ ...f, budget_min: parsed }));
                    }} 
                    placeholder="e.g., 50,000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Budget Max</label>
                  <Input 
                    type="text"
                    value={formatNumberWithCommas(eventForm.budget_max)} 
                    onChange={(e) => {
                      const parsed = parseFormattedNumber(e.target.value);
                      setEventForm(f => ({ ...f, budget_max: parsed }));
                    }} 
                    placeholder="e.g., 100,000"
                  />
                </div>
              </div>

              {/* Currency, Temperature Unit, Distance Unit */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Currency</label>
                  <Select
                    value={eventForm.base_currency || 'USD'}
                    onChange={(e) => setEventForm(f => ({ ...f, base_currency: e.target.value }))}
                  >
                    {currencyOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Temperature Unit</label>
                  <Select
                    value={eventForm.temperature_unit || 'celsius'}
                    onChange={(e) => setEventForm(f => ({ ...f, temperature_unit: e.target.value as 'celsius' | 'fahrenheit' }))}
                  >
                    {temperatureUnitOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Distance Unit</label>
                  <Select
                    value={eventForm.distance_unit || 'kilometers'}
                    onChange={(e) => setEventForm(f => ({ ...f, distance_unit: e.target.value as 'kilometers' | 'miles' }))}
                  >
                    {distanceUnitOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
            </TabsContent>

            {/* Venue Requirements Tab */}
            <TabsContent value="venue-requirements" className="space-y-4">
              <p className="text-xs text-gray-600 mb-4">
                These filters will pre-populate when using the research/proposal tool (OpenNeo) for this event.
              </p>
              
              {/* Min Rooms */}
              <Input
                label="Minimum Rooms"
                type="number"
                min={0}
                value={filters.min_rooms ?? ''}
                onChange={(e) =>
                  updateFilter('min_rooms', e.target.value ? Number(e.target.value) : null)
                }
                placeholder="e.g., 80"
              />

              {/* Conference Capacity & Star Rating */}
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Min Conference Capacity"
                  type="number"
                  min={0}
                  value={filters.min_conference_capacity ?? ''}
                  onChange={(e) =>
                    updateFilter('min_conference_capacity', e.target.value ? Number(e.target.value) : null)
                  }
                  placeholder="e.g., 200"
                />
                <div>
                  <label htmlFor="star-rating" className="block text-sm font-medium mb-1">
                    Minimum Star Rating
                  </label>
                  <Select
                    id="star-rating"
                    value={filters.star_rating?.toString() || ''}
                    onChange={(e) =>
                      updateFilter('star_rating', e.target.value ? Number(e.target.value) : null)
                    }
                  >
                    <option value="">Any</option>
                    <option value="3">3+ Stars</option>
                    <option value="4">4+ Stars</option>
                    <option value="5">5 Stars</option>
                  </Select>
                </div>
              </div>

              {/* Airport Distance */}
              <Input
                label="Max Airport Distance (km)"
                type="number"
                min={0}
                value={filters.max_airport_distance_km ?? ''}
                onChange={(e) =>
                  updateFilter('max_airport_distance_km', e.target.value ? Number(e.target.value) : null)
                }
                placeholder="e.g., 30"
              />

              {/* Amenities */}
              <div className="space-y-3">
                <h5 className="text-sm font-medium text-gray-900">Amenities</h5>
                
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="has_spa"
                      checked={filters.has_spa === true}
                      onChange={(e) => updateFilter('has_spa', e.target.checked ? true : null)}
                    />
                    <label htmlFor="has_spa" className="text-sm text-gray-700 cursor-pointer">
                      Spa Facilities
                    </label>
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="all_inclusive"
                      checked={filters.all_inclusive === true}
                      onChange={(e) => updateFilter('all_inclusive', e.target.checked ? true : null)}
                    />
                    <label htmlFor="all_inclusive" className="text-sm text-gray-700 cursor-pointer">
                      All-Inclusive Available
                    </label>
                  </div>
                </div>

                {/* Count-Based Amenities */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <Input
                    label="Minimum Restaurants"
                    type="number"
                    min={0}
                    value={filters.min_restaurants ?? ''}
                    onChange={(e) =>
                      updateFilter('min_restaurants', e.target.value ? Number(e.target.value) : null)
                    }
                    placeholder="e.g., 2"
                  />

                  <Input
                    label="Minimum Bars/Lounges"
                    type="number"
                    min={0}
                    value={filters.min_bars ?? ''}
                    onChange={(e) =>
                      updateFilter('min_bars', e.target.value ? Number(e.target.value) : null)
                    }
                    placeholder="e.g., 1"
                  />

                  <Input
                    label="Minimum Pools"
                    type="number"
                    min={0}
                    value={filters.min_pools ?? ''}
                    onChange={(e) =>
                      updateFilter('min_pools', e.target.value ? Number(e.target.value) : null)
                    }
                    placeholder="e.g., 1"
                  />

                  <div>
                    <label htmlFor="min_tripadvisor_rating" className="block text-sm font-medium text-gray-700 mb-1">
                      Min TripAdvisor Rating
                    </label>
                    <Select
                      id="min_tripadvisor_rating"
                      value={filters.min_tripadvisor_rating?.toString() || ''}
                      onChange={(e) =>
                        updateFilter('min_tripadvisor_rating', e.target.value ? Number(e.target.value) : null)
                      }
                    >
                      <option value="">Any rating</option>
                      <option value="3.0">3.0+ stars</option>
                      <option value="3.5">3.5+ stars</option>
                      <option value="4.0">4.0+ stars</option>
                      <option value="4.5">4.5+ stars</option>
                      <option value="5.0">5.0 stars</option>
                    </Select>
                  </div>
                </div>

                {/* Enum-Based Amenities */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label htmlFor="beach_access" className="block text-sm font-medium text-gray-700 mb-1">
                      Beach Access
                    </label>
                    <Select
                      id="beach_access"
                      value={filters.beach_access || ''}
                      onChange={(e) => updateFilter('beach_access', (e.target.value as any) || null)}
                    >
                      <option value="">Any</option>
                      <option value="beachfront">Beachfront</option>
                      <option value="beach_access">Beach Access</option>
                      <option value="none">No Beach Access</option>
                    </Select>
                  </div>

                  <div>
                    <label htmlFor="property_type" className="block text-sm font-medium text-gray-700 mb-1">
                      Property Type
                    </label>
                    <Select
                      id="property_type"
                      value={filters.property_type || ''}
                      onChange={(e) => updateFilter('property_type', (e.target.value as any) || null)}
                    >
                      <option value="">Any</option>
                      <option value="adults_only">Adults Only</option>
                      <option value="family_friendly">Family Friendly</option>
                      <option value="mixed">Mixed</option>
                    </Select>
                  </div>

                  <div>
                    <label htmlFor="has_golf" className="block text-sm font-medium text-gray-700 mb-1">
                      Golf Course
                    </label>
                    <Select
                      id="has_golf"
                      value={filters.has_golf === true ? 'yes' : ''}
                      onChange={(e) => updateFilter('has_golf', e.target.value === 'yes' ? true : null)}
                    >
                      <option value="">Any</option>
                      <option value="yes">On-site or Nearby</option>
                    </Select>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Destinations Tab */}
            <TabsContent value="destinations" className="space-y-4">
              {loadingDestinations ? (
                <div className="flex items-center justify-center py-8">
                  <Spinner size="md" />
                </div>
              ) : destinations.length === 0 ? (
                <div className="text-center py-8">
                  <MapPin className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No destinations set
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Destinations are set in the Research and Proposal tool.
                  </p>
                  <Button
                    variant="primary"
                    onClick={() => {
                      closeEditModal();
                      navigate(`/tools/research-proposal/${eventId}`);
                    }}
                    leftIcon={<Sparkles className="w-4 h-4" />}
                  >
                    Build Proposal
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {destinations.map((destination) => {
                    // Format location name: use city, country format, or just country if it's a country destination
                    const locationName = destination.city === destination.country
                      ? destination.country
                      : `${destination.city}, ${destination.country}`;
                    
                    const searchModeLabel = destination.search_mode === 'radius' 
                      ? `Radius: ${destination.search_radius_km}km`
                      : 'City Name Match';
                    
                    return (
                      <Card key={destination.id}>
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between gap-4">
                            {/* Left: Title */}
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-gray-900 truncate">{locationName}</h4>
                            </div>
                            
                            {/* Right: Badges and Statistics */}
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Badge variant="secondary" className="text-xs whitespace-nowrap">
                                {searchModeLabel}
                              </Badge>
                              {destination.total_in_database !== undefined && destination.total_in_database > 0 && (
                                <Badge variant="secondary" className="text-xs whitespace-nowrap">
                                  {destination.total_in_database} in DB
                                </Badge>
                              )}
                              {destination.shortlist_count > 0 && (
                                <Badge variant="secondary" className="text-xs whitespace-nowrap">
                                  {destination.shortlist_count} shortlisted
                                </Badge>
                              )}
                              <Badge variant="secondary" className="text-xs whitespace-nowrap">
                                {destination.accepted_count || 0} accepted
                              </Badge>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                  <div className="pt-2">
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        closeEditModal();
                        navigate(`/tools/research-proposal/${eventId}`);
                      }}
                      leftIcon={<Sparkles className="w-4 h-4" />}
                    >
                      Manage Destinations
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
          
          <div className="flex justify-end gap-2 pt-6 mt-6 border-t">
            <Button type="button" variant="ghost" onClick={closeEditModal} disabled={eventSaving}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="primary" 
              isLoading={eventSaving}
              disabled={!eventForm.name.trim()}
            >
              Save Changes
            </Button>
          </div>
        </form>
      </Modal>

      {/* Proposal Feedback Modal */}
      <Modal 
        isOpen={feedbackModalOpen} 
        onClose={() => setFeedbackModalOpen(false)} 
        title="Proposal Feedback"
        description="Feedback submitted by clients for this proposal"
        size="lg"
      >
        {loadingFeedback ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="md" />
          </div>
        ) : feedback.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="w-12 h-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">No feedback has been submitted yet.</p>
          </div>
        ) : (
          <div className="space-y-4 max-h-[600px] overflow-y-auto">
            {feedback.map((item) => (
              <Card key={item.id}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-semibold text-gray-900">{item.name}</h4>
                      <p className="text-sm text-gray-500 mt-1">
                        {new Date(item.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{item.comments}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </Modal>
    </PageContainer>
  );
}
